/**
 * Value fine-tuning of the note lane's selection (W2-03 note-lane; DESIGN.md §5.4 "value operations to their kind",
 * §8.3 RPE preset Ctrl+wheel; RPE NOTE-13, EVT-11).
 *
 *  - Width: `fineTuneWidth` changes the selected notes' width (`size`) by WIDTH_STEP per step, as ONE undoable step
 *    per burst (coalesced on the selection), refused on locked lines. RPE's Ctrl+wheel width was not undoable; here
 *    it is (NOTE-13). The lane's wheel handler (RPE preset, Ctrl+wheel over a lane) and `notes.wheelWidth` use it.
 *  - Key values: `keyValueEdits` computes the edits that move the selected keyframes' values by friendly steps (the
 *    same steps the motion columns' value nudges use: 1 px, 1 degree, 1 %, 0.1 speed / thickness), so Left / Right
 *    on a selection mixing notes and keyframes move the notes in X and the keyframes in value in one step.
 */

import type { KeyId, LineProp, NoteId, UnitContext } from '../../core/model/types';
import type { KeyValuePart } from '../../core/commands/types';
import { Units } from '../../core/model/units';
import { isUiMover, kindOfLine } from '../../core/model/lineKind';
import { Core } from '../../core/commands/index';
import { clampToDomain } from '../../core/commands/keyValueRules';
import type { AppServices } from '../../app/registry/types';
import type { DocStore } from '../../state/types';
import { m } from './noteLane.messages';
import { lockedReason, ref, selectedItems } from './laneSession';
import { WHEEL_NOTCH_PX } from '../../core/util/wheel';

/** Width change per step (raw multiplier: 0.1 = 10 % of the normal width). */
export const WIDTH_STEP = 0.1;
/** Narrowest width the fine-tune reaches (a narrower imported width is never widened by narrowing). */
export const WIDTH_MIN = 0.1;

function round3(v: number): number {
  const r = Math.round(v * 1000) / 1000;
  return Object.is(r, -0) ? 0 : r;
}

/** The width a note gets after `steps` steps (never below WIDTH_MIN unless it already was). */
export function tunedWidth(size: number, steps: number): number {
  const next = round3(size + WIDTH_STEP * steps);
  return next < WIDTH_MIN ? Math.min(size, WIDTH_MIN) : next;
}

/**
 * Steps of a wheel event or a command argument: a number, `{ steps }`, or the keyboard router's `{ wheel }` (wheel
 * up = positive = wider). Every wheel tick counts at least one step.
 */
export function stepsOf(args: unknown): number {
  if (typeof args === 'number' && Number.isFinite(args)) return Math.trunc(args);
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return 1;
  const o = args as Readonly<Record<string, unknown>>;
  if (typeof o.steps === 'number' && Number.isFinite(o.steps)) return Math.trunc(o.steps);
  const w = o.wheel;
  if (typeof w === 'object' && w !== null && !Array.isArray(w)) {
    const dy = (w as Readonly<Record<string, unknown>>).dy;
    if (typeof dy === 'number' && Number.isFinite(dy)) return wheelSteps(dy);
  }
  return 1;
}

/** Steps of a normalised vertical wheel delta (px): wheel up (negative delta) = positive steps. */
export function wheelSteps(dyPx: number): number {
  if (dyPx === 0 || !Number.isFinite(dyPx)) return 0;
  return -Math.sign(dyPx) * Math.max(1, Math.round(Math.abs(dyPx) / WHEEL_NOTCH_PX));
}

/**
 * Wheel steps of a stream of events: a notch-sized delta (a mouse wheel) is at least one step at once, small deltas
 * (smooth scrolling) add up within a burst (300 ms) until they make a notch.
 */
export function createWheelStepper(): (dyPx: number, now: number) => number {
  let acc = 0;
  let at = Number.NEGATIVE_INFINITY;
  return (dy, now) => {
    if (now - at > 300) acc = 0;
    at = now;
    if (Math.abs(dy) >= 20) {
      acc = 0;
      return wheelSteps(dy);
    }
    acc += dy;
    const notches = Math.trunc(acc / WHEEL_NOTCH_PX);
    acc -= notches * WHEEL_NOTCH_PX;
    return notches === 0 ? 0 : -notches;
  };
}

/** Vertical delta of a wheel event in px (deltaMode lines / pages normalised). */
export function wheelDeltaPx(e: Pick<WheelEvent, 'deltaY' | 'deltaMode'>, pagePx = 800): number {
  return e.deltaMode === 1 ? e.deltaY * 40 : e.deltaMode === 2 ? e.deltaY * pagePx : e.deltaY;
}

/** A trackpad pinch (zooms time in every preset; never a fine-tune). */
export function isPinch(e: Pick<WheelEvent, 'ctrlKey' | 'deltaMode' | 'deltaY'>): boolean {
  return e.ctrlKey && e.deltaMode === 0 && !Number.isInteger(e.deltaY);
}

export type TuneResult = 'done' | 'unchanged' | 'noNotes' | 'locked' | 'failed';

/**
 * Changes the selected notes' width by `steps` steps (one undoable step per burst: coalesced on the selection).
 * Toasts why when refused; returns what happened.
 */
export function fineTuneWidth(services: AppServices, steps: number, ids?: readonly NoteId[]): TuneResult {
  const store = services.store;
  if (store === null) return 'noNotes';
  const notes = ids ?? selectedItems(services).notes;
  if (notes.length === 0) return 'noNotes';
  if (store.gestureActive) {
    services.ui.toast(ref(m.reasonGesture), { kind: 'info' });
    return 'failed';
  }
  const locked = lockedReason(services, { notes });
  if (locked !== null) {
    services.ui.toast(locked, { kind: 'warning' });
    return 'locked';
  }
  const edits: Array<{ readonly id: NoteId; readonly size: number }> = [];
  for (const id of notes) {
    const n = store.doc.notes.get(id);
    if (n === undefined) continue;
    const size = tunedWidth(n.size, steps);
    if (size !== n.size) edits.push({ id, size });
  }
  if (edits.length === 0) return 'unchanged';
  const key = [...notes].sort((a, b) => a - b).join(',');
  const out = store.transact(
    { label: ref(m.txWidth, { count: edits.length }), command: 'notes.wheelWidth', coalesceKey: `wheel:notes:${key}` },
    (tx) => {
      for (const e of edits) Core.notes.patchNotes(tx, { ids: [e.id], patch: { size: e.size } });
    },
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return 'failed';
  }
  return 'done';
}

/** One friendly value step of a line property in raw units (1 px / degree / %, 0.1 speed or thickness). */
export function valueStepOf(prop: LineProp, ctx: UnitContext): number {
  const unit = Units.unitFor(prop, ctx);
  const friendly = unit === 'speed' || unit === 'thickness' ? 0.1 : 1;
  return Math.abs(Units.fromFriendly(prop, friendly, ctx) - Units.fromFriendly(prop, 0, ctx));
}

export interface KeyValueEdit {
  readonly key: KeyId;
  readonly part: KeyValuePart;
  readonly value: number;
}

/**
 * Edits moving the numeric values of `keys` by `times` friendly steps. A jump key moves both of its values (the
 * arrive value only when the previous segment is not a Stay, which keeps its stayed value); other keys stay
 * continuous. Non-numeric keys (colour, text) and keys of non-line tracks are skipped.
 */
export function keyValueEdits(
  store: Pick<DocStore, 'doc' | 'idx'>,
  keys: readonly KeyId[],
  times: number,
): KeyValueEdit[] {
  const out: KeyValueEdit[] = [];
  if (times === 0) return out;
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    const owner = k === undefined ? undefined : store.doc.tracks.get(k.track)?.owner;
    if (k === undefined || owner?.kind !== 'line' || typeof k.vIn !== 'number' || typeof k.vOut !== 'number') continue;
    const rec = store.doc.lines.get(owner.line);
    if (rec === undefined) continue;
    const step = valueStepOf(owner.prop, { kind: kindOfLine(store.doc, store.idx, rec), uiMover: isUiMover(rec) });
    const delta = step * times;
    // Relative edits stay inside the property's domain (UX-042: opacity 0..255).
    const moved = (v: number): number => roundValue(clampToDomain(owner.prop, v, v + delta));
    const prev = store.idx.prevKey(id);
    if (prev !== undefined && k.vIn !== k.vOut) {
      out.push({ key: id, part: 'out', value: moved(k.vOut) });
      if (store.doc.keys.get(prev)?.ease.kind !== 'hold') out.push({ key: id, part: 'in', value: moved(k.vIn) });
    } else out.push({ key: id, part: 'both', value: moved(k.vOut) });
  }
  return out;
}

function roundValue(v: number): number {
  const r = Math.round(v * 1e6) / 1e6;
  return Object.is(r, -0) ? 0 : r;
}
